import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import { getReadingTime } from '../lib/readingTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const currentLang = location.pathname.startsWith('/de') ? 'de' : 'en';
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug, currentLang]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/blog/posts/${slug}`, {
        params: { lang: currentLang },
      });
      setPost(response.data);
    } catch (err) {
      console.error('Failed to fetch blog post:', err);
      setError(err.response?.status === 404 ? 'notFound' : 'error');
    } finally {
      setLoading(false);
    }
  };

  const content = {
    en: {
      backToBlog: 'Back to Blog',
      loading: 'Loading article...',
      notFound: 'Article not found',
      notFoundDesc: 'The article you\'re looking for doesn\'t exist or has been removed.',
      error: 'Failed to load article',
      errorDesc: 'Something went wrong. Please try again later.',
      share: 'Share',
    },
    de: {
      backToBlog: 'Zurück zum Blog',
      loading: 'Artikel wird geladen...',
      notFound: 'Artikel nicht gefunden',
      notFoundDesc: 'Der gesuchte Artikel existiert nicht oder wurde entfernt.',
      error: 'Artikel konnte nicht geladen werden',
      errorDesc: 'Etwas ist schief gelaufen. Bitte versuchen Sie es später erneut.',
      share: 'Teilen',
    },
  };

  const t = content[currentLang];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return currentLang === 'en'
      ? date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title[currentLang],
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="min-h-screen pt-32 pb-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
          </div>
          <p className="text-xl text-gray-600 mt-8">{t.loading}</p>
        </div>
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main className="min-h-screen pt-32 pb-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">
            {error === 'notFound' ? t.notFound : t.error}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {error === 'notFound' ? t.notFoundDesc : t.errorDesc}
          </p>
          <Button
            onClick={() => navigate(`/${currentLang}/blog`)}
            variant="outline"
            className="font-semibold"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t.backToBlog}
          </Button>
        </div>
      </main>
    );
  }

  if (!post) return null;

  const postTitle = post.title[currentLang];
  const postExcerpt = post.excerpt[currentLang];
  const postContent = post.content[currentLang];

  const postStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": postTitle,
    "description": postExcerpt,
    "image": post.image_url,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Podcast Zurich",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_267f958e-8a2e-4210-b49e-6bd976382e6d/artifacts/4hmotnvg_Bildschirmfoto%202026-02-26%20um%2000.36.52.png"
      }
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://podcastzurich.com/${currentLang}/blog/${slug}`
    }
  };

  return (
    <>
      <SEOHead
        title={`${postTitle} | Podcast Zurich Blog`}
        description={postExcerpt}
        canonicalPath={`/${currentLang}/blog/${slug}`}
        ogImage={post.image_url}
        ogType="article"
        structuredData={postStructuredData}
      />
      <main className="min-h-screen pt-24 pb-20 bg-white">
        {/* Hero Image */}
        <div className="w-full h-[400px] relative overflow-hidden bg-gray-200">
          <img
            src={post.image_url}
            alt={postTitle}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
          {/* Back Button */}
          <Button
            onClick={() => navigate(`/${currentLang}/blog`)}
            variant="outline"
            className="mb-6 bg-white font-semibold"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            {t.backToBlog}
          </Button>

          {/* Article Card */}
          <div className="bg-white rounded-xl shadow-xl p-8 md:p-12">
            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-gray-900">
              {postTitle}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.created_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2" data-testid="reading-time">
                <Clock className="w-5 h-5" />
                <span>{getReadingTime(postContent, currentLang)}</span>
              </div>
              <Button
                onClick={handleShare}
                variant="ghost"
                size="sm"
                className="ml-auto"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t.share}
              </Button>
            </div>

            {/* Excerpt */}
            <p className="text-xl md:text-2xl text-gray-700 mb-10 font-medium leading-relaxed italic border-l-4 border-yellow-300 pl-5">
              {postExcerpt}
            </p>

            {/* Content */}
            <div
              className="article-content prose prose-lg lg:prose-xl max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:tracking-tight
                prose-h1:text-4xl prose-h1:mt-12 prose-h1:mb-6 prose-h1:leading-tight
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-tight prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
                prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-gray-800
                prose-h4:text-xl prose-h4:mt-8 prose-h4:mb-3
                prose-p:text-gray-700 prose-p:leading-[1.8] prose-p:mb-6 prose-p:text-lg
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-a:text-yellow-700 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                prose-ul:my-6 prose-ul:space-y-2 prose-li:text-gray-700 prose-li:leading-relaxed prose-li:text-lg
                prose-ol:my-6 prose-ol:space-y-2
                prose-blockquote:border-l-4 prose-blockquote:border-yellow-300 prose-blockquote:bg-yellow-50/40 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:font-medium prose-blockquote:text-gray-800 prose-blockquote:rounded-r-lg
                prose-img:rounded-xl prose-img:shadow-md prose-img:my-8
                prose-code:bg-gray-100 prose-code:text-gray-900 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-code:font-medium
                prose-pre:bg-gray-900 prose-pre:rounded-xl prose-pre:p-5
                prose-hr:my-12 prose-hr:border-gray-200"
              dangerouslySetInnerHTML={{ __html: postContent }}
            />
          </div>

          {/* Back to Blog CTA */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate(`/${currentLang}/blog`)}
              style={{ backgroundColor: '#ffff31', color: '#000' }}
              className="font-semibold hover:opacity-90"
              data-testid="back-to-blog-cta"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              {t.backToBlog}
            </Button>
          </div>
        </article>
      </main>
    </>
  );
};

export default BlogPost;
